import { FormControl, Validators } from '@angular/forms';
import { ParcelAppEntityParcelWrite, RoadOccupationStageAppEntityApplicationRead } from '../../../../../../../openapi';
import { calcDaysBetweenDates } from '../../../../../../../services/helpers';
import { CustomValidator, fromToDate } from '../../../../../../../common/forms/custom.validators';
import {
  RoadControlsForm,
  RoadControlsFormValue
} from '../../../../common/components/road-controls-set/road-controls.form';
import {
  ExposedMapDictionary,
  ExposedMapDictRate
} from '../../../../../../../common/exposed-map/exposed-map.component';
import { environment } from '../../../../../../../../environments/environment';

export function toStageFormValue(v: RoadOccupationStageAppEntityApplicationRead): OccupationOfLanePlacementConstructionObjectsStageFormValue {
  return {
    startDate: v?.startDate || null,
    endDate: v?.endDate || null,
    dayQuantity: v?.dayQuantity || 0,

    roadNumber: v?.roadNumber || '',
    roadName: v?.roadName || '',
    street: v?.street || '',

    foreignDeviceLength: (v?.foreignDeviceLength || 0).toFixed(2).replace('.', ','),
    foreignDeviceWidth: (v?.foreignDeviceWidth || 0).toFixed(2).replace('.', ','),
    foreignDeviceArea: (v?.foreignDeviceArea || 0).toFixed(2).replace('.', ','),
    geometry: v?.geometry || '',

    parcels: v?.parcels || []
  };
}

export interface OccupationOfLanePlacementConstructionObjectsStageFormValue extends RoadControlsFormValue {
  roadName: string;
  roadNumber: string;
  street: string;
  startDate: Date | null;
  endDate: Date | null;
  dayQuantity: number;

  foreignDeviceLength: string;
  foreignDeviceWidth: string;
  foreignDeviceArea: string;

  geometry: string;

  parcels: ParcelAppEntityParcelWrite[];
}

export class OccupationOfLanePlacementConstructionObjectsStageForm extends RoadControlsForm {
  constructor() {
    super({
      startDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat(), CustomValidator.minDate(new Date())] }),
      endDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat()] }),
      dayQuantity: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
      geometry: new FormControl(null, environment.production ? Validators.required : []),

      foreignDeviceLength: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      foreignDeviceWidth: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      foreignDeviceArea: new FormControl(
        { value: '', disabled: true },
        {
          validators: [Validators.maxLength(255)],
          nonNullable: true
        }
      ),

      parcels: new FormControl([], {
        nonNullable: true,
        validators: [
          Validators.minLength(1),
          Validators.required
        ]
      })
    }, {
      validators: [
        fromToDate('startDate', 'endDate')
      ]
    });
  }

  public onLoadExposedMapDict(v: ExposedMapDictionary): void {
    const rates: ExposedMapDictRate[] = v.rates;
    const foreignDeviceArea = rates.filter(v => [260, 274].includes(v.rateId)).at(0)?.area;
    if (foreignDeviceArea) {
      this.foreignDeviceLength.setValue('', { emitEvent: false });
      this.foreignDeviceWidth.setValue('', { emitEvent: false });
      this.foreignDeviceArea.setValue(foreignDeviceArea);
    }
  }

  public get foreignDeviceLength(): FormControl {
    return this.get('foreignDeviceLength') as FormControl;
  }

  public get foreignDeviceWidth(): FormControl {
    return this.get('foreignDeviceWidth') as FormControl;
  }

  public get foreignDeviceArea(): FormControl {
    return this.get('foreignDeviceArea') as FormControl;
  }

  public get canClearForeignDeviceControls(): boolean {
    return this.foreignDeviceLength.value || this.foreignDeviceWidth.value || this.foreignDeviceArea.value;
  }

  public clearForeignDeviceControls(): void {
    this.foreignDeviceWidth.setValue('', { emitEvent: false });
    this.foreignDeviceLength.setValue('', { emitEvent: false });
    this.foreignDeviceArea.setValue('', { emitEvent: false });
  }

  public calcArea(): void {
    let width = this.foreignDeviceWidth.value;
    let length = this.foreignDeviceLength.value;
    if (!width || !length) {
      return;
    }
    width = (width?.toString() || '0').replaceAll(',', '.').replaceAll(' ', '');
    length = (length?.toString() || '0').replaceAll(',', '.').replaceAll(' ', '');
    this.foreignDeviceArea.setValue((Number(width) * Number(length)).toFixed(2).replaceAll('.', ','));
  }

  public get startDate(): FormControl {
    return this.get('startDate') as FormControl;
  }

  public get endDate(): FormControl {
    return this.get('endDate') as FormControl;
  }

  public get dayQuantity(): FormControl {
    return this.get('dayQuantity') as FormControl;
  }

  public startDateChange(v: Date): void {
    if (v && this.endDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(v), new Date(this.endDate.value))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public endDateChange(v: Date): void {
    if (v && this.startDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(this.startDate.value), new Date(v))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public get geometry(): FormControl {
    return this.get('geometry') as FormControl;
  }

  public get values(): OccupationOfLanePlacementConstructionObjectsStageFormValue {
    return {
      ...this.value,
      roadNumber: this.roadNumber.value,
      roadName: this.roadName.value,
      street: this.street.value,
      foreignDeviceArea: this.foreignDeviceArea.value
    };
  }
}
